import { KeyboardEvent, Ref } from "react";
import { Box, CircularProgress, IconButton, InputBase, Tooltip, Typography, useTheme } from "@mui/material";
import { ArrowUpward } from "@mui/icons-material";

const MAX_MESSAGE_CHARS = 2000;
const COUNTER_THRESHOLD = 1800;

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** Blocks sending (e.g. while a reply is generating) but typing stays enabled. */
  disabled?: boolean;
  /** Free-tier lock: the whole input is disabled with the upgrade tooltip. */
  locked?: boolean;
  /** Shows a spinner in the send button (e.g. while the conversation is created). */
  pending?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLTextAreaElement>;
}

/**
 * The single chat composer, shared by the /chat hero and the in-conversation
 * footer so the input is identical across both. Controlled — the parent owns
 * the text. The background is solid (not translucent) so chat content scrolling
 * beneath the floating conversation composer never ghosts through it.
 */
export const ChatComposer = ({
  value,
  onChange,
  onSend,
  disabled = false,
  locked = false,
  pending = false,
  placeholder = "How can I help with your research?",
  autoFocus = false,
  inputRef,
}: ChatComposerProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const canSend = value.trim().length > 0 && !disabled && !locked && !pending;
  const showCounter = !locked && value.length >= COUNTER_THRESHOLD;
  const atLimit = value.length >= MAX_MESSAGE_CHARS;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  const interior = isDark ? "#232323" : "#f7f7f7";
  const greenBorderColor = isDark ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.45)";
  const neutralBorderColor = isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)";

  return (
    <Box>
      <Tooltip title={locked ? "Available with Insight Pro" : ""} arrow>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            p: "8px 8px 8px 18px",
            borderRadius: "16px",
            opacity: locked ? 0.6 : 1,
            backgroundColor: interior,
            // Neutral by default; greens only on focus (&:focus-within below).
            border: `1px solid ${neutralBorderColor}`,
            transition: "border-color 0.2s ease",
            "&:focus-within": { borderColor: greenBorderColor },
          }}
        >
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            autoFocus={autoFocus && !locked}
            inputRef={inputRef}
            disabled={locked}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: "1rem", lineHeight: 1.5, py: 0.5, textAlign: "left" }}
          />
          <IconButton
            onClick={onSend}
            disabled={!canSend}
            aria-label="Send message"
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              backgroundColor: "#22c55e",
              color: "white",
              transition: "background-color 0.2s ease",
              "&:hover": { backgroundColor: "#16a34a" },
              "&:active": { transform: "scale(0.96)" },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {pending && <CircularProgress size={16} sx={{ color: "inherit" }} />}
            {!pending && <ArrowUpward sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Tooltip>

      {showCounter && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            mt: 0.75,
            fontVariantNumeric: "tabular-nums",
            color: atLimit ? "#f59e0b" : theme.palette.text.secondary,
          }}
        >
          {value.length} / {MAX_MESSAGE_CHARS}
        </Typography>
      )}
    </Box>
  );
};
